import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MENU_CONFIG, MenuMacro, MenuGroup } from '../config/menu';
import { usePermesso } from '../context/PermessiContext';
import { useSocieta } from '../context/SocietaContext';
import { useMenuPreferiti } from '../hooks/useMenuPreferiti';
import { useScrollPersistente } from '../hooks/useScrollPersistente';
import Topbar from './layout/Topbar';
import './Layout.css';

// ── Icone UI interne ───────────────────────────────────────────────────────
const UIIcon = {
  ChevronDown: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>,
  Star: ({ filled }: { filled?: boolean }) => <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
  // Doppio chevron: su = comprimi tutto, giù = espandi tutto
  ChevronsUp: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 11 12 6 7 11" /><polyline points="17 18 12 13 7 18" /></svg>,
  ChevronsDown: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 13 12 18 17 13" /><polyline points="7 6 12 11 17 6" /></svg>,
  // Pannello con freccia a sinistra: chiudi la sidebar
  PanelClose: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><polyline points="16 15 13 12 16 9" /></svg>,
};

// Sotto i 768px la sidebar è un drawer a scomparsa (vedi Layout.css)
const isMobileViewport = () => window.matchMedia('(max-width: 767px)').matches;

// ── Tutti i path dichiarati nel menu (per il calcolo "best match") ───────
const ALL_MENU_PATHS: string[] = (() => {
  const paths: string[] = [];
  for (const macro of MENU_CONFIG) {
    if (macro.path) paths.push(macro.path);
    macro.groups?.forEach(g => g.items.forEach(item => paths.push(item.path)));
  }
  return paths;
})();

// ── Trova il path di menu più specifico che corrisponde a quello corrente ──
// Why: con un semplice startsWith più voci si "accendono" insieme quando i
// loro path condividono un prefisso (es. /anagrafiche/articoli e
// /anagrafiche/articoli/listini-fornitori). La regola corretta è:
// match con confine "/" e, fra più candidati validi, vince il più lungo.
function findActivePath(pathname: string): string {
  let best = '';
  for (const path of ALL_MENU_PATHS) {
    const matches = pathname === path || pathname.startsWith(path + '/');
    if (matches && path.length > best.length) best = path;
  }
  return best;
}

// ── Helper per trovare label attiva ─────────────────────────────────────────
function getActiveLabel(activePath: string): string {
  for (const macro of MENU_CONFIG) {
    if (macro.path && macro.path === activePath) return macro.label;
    if (macro.groups) {
      for (const group of macro.groups) {
        for (const item of group.items) {
          if (item.path === activePath) return item.label;
        }
      }
    }
  }
  return 'Backoffice';
}

// ── Verifica se un Macro o Gruppo contiene il path attivo ─────────────────
function isMacroActive(macro: MenuMacro, activePath: string): boolean {
  if (macro.path && macro.path === activePath) return true;
  return !!macro.groups?.some(g => isGroupActive(g, activePath));
}

function isGroupActive(group: MenuGroup, activePath: string): boolean {
  return group.items.some(item => item.path === activePath);
}

// ── Evidenzia il testo corrispondente ─────────────────────────────────────
function highlight(text: string, query: string): React.ReactNode {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong className="bop-layout__highlight">
        {text.slice(idx, idx + query.length)}
      </strong>
      {text.slice(idx + query.length)}
    </>
  );
}

// ── Toast 403 ─────────────────────────────────────────────────────────────
function Toast403({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className="bop-layout__toast-403">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bop-layout__toast-icon">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div className="bop-layout__toast-content">
        <div className="bop-layout__toast-title">Accesso negato</div>
        <div className="bop-layout__toast-message">{message}</div>
      </div>
      <button onClick={onClose} className="bop-layout__toast-close">✕</button>
    </div>
  );
}

// ── Componente Layout ─────────────────────────────────────────────────────
interface Props {
  children: React.ReactNode;
  title?: string;
}

export default function Layout({ children, title }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { utente, loading: permLoading, can } = usePermesso();
  const { coloreAccento, societaAttiva } = useSocieta();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Sidebar chiusa su desktop (scelta persistita: vale per tutte le pagine)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('bop:menu:collapsed') === '1'; } catch { return false; }
  });
  const setCollapsed = (v: boolean) => {
    setSidebarCollapsed(v);
    try { localStorage.setItem('bop:menu:collapsed', v ? '1' : '0'); } catch {}
  };
  const [menuSearch, setMenuSearch] = useState('');
  const [toast403, setToast403] = useState<string | null>(null);
  // Ogni pagina monta il proprio Layout: senza questo la sidebar torna in cima a ogni navigazione
  const sidebarRef = useScrollPersistente<HTMLElement>('bop:menu:scroll');

  // ── Preferiti menu ── tab attiva persistita: si riapre sull'ultima usata
  const { preferiti, togglePreferito } = useMenuPreferiti();
  const preferitiSet = useMemo(() => new Set(preferiti), [preferiti]);
  const [menuTab, setMenuTab] = useState<'menu' | 'preferiti'>(() => {
    try {
      return localStorage.getItem('bop:menu:tab') === 'preferiti' ? 'preferiti' : 'menu';
    } catch {
      return 'menu';
    }
  });
  const switchMenuTab = (tab: 'menu' | 'preferiti') => {
    setMenuTab(tab);
    try { localStorage.setItem('bop:menu:tab', tab); } catch {}
  };

  // Stato espansione Menu — chiavi STABILI basate su label, non sugli indici
  // (gli indici di MENU_CONFIG e visibleConfig divergono quando i permessi
  // filtrano alcune macro, causando key-mismatch). Stato persistito in
  // sessionStorage per sopravvivere ai rimontaggi di <Layout> ad ogni
  // cambio di route.
  const macroKey = (macro: { label: string }) => `macro:${macro.label}`;
  const groupKey = (macro: { label: string }, group: { label: string }) =>
    `group:${macro.label}::${group.label}`;

  const computeActiveExpansion = (pathname: string) => {
    const ap = findActivePath(pathname);
    const macros: Record<string, boolean> = {};
    const groups: Record<string, boolean> = {};
    MENU_CONFIG.forEach(macro => {
      if (isMacroActive(macro, ap)) {
        macros[macroKey(macro)] = true;
        macro.groups?.forEach(group => {
          if (isGroupActive(group, ap)) groups[groupKey(macro, group)] = true;
        });
      }
    });
    return { macros, groups };
  };

  const readPersisted = (storageKey: string): Record<string, boolean> => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const [expandedMacros, setExpandedMacros] = useState<Record<string, boolean>>(() => {
    const init = computeActiveExpansion(location.pathname);
    return { ...readPersisted('bop:menu:expandedMacros'), ...init.macros };
  });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const init = computeActiveExpansion(location.pathname);
    return { ...readPersisted('bop:menu:expandedGroups'), ...init.groups };
  });

  // Persiste lo stato di espansione ad ogni cambio
  useEffect(() => {
    try { sessionStorage.setItem('bop:menu:expandedMacros', JSON.stringify(expandedMacros)); } catch {}
  }, [expandedMacros]);
  useEffect(() => {
    try { sessionStorage.setItem('bop:menu:expandedGroups', JSON.stringify(expandedGroups)); } catch {}
  }, [expandedGroups]);

  // Ascolta eventi 403 da api.ts
  useEffect(() => {
    const handler = (e: Event) => {
      setToast403((e as CustomEvent<{ message: string }>).detail.message);
    };
    window.addEventListener('bop:403', handler);
    return () => window.removeEventListener('bop:403', handler);
  }, []);

  // Redirect se non autenticato
  useEffect(() => {
    if (!permLoading && !utente) {
      const token = localStorage.getItem('token');
      if (!token) window.location.replace('/');
    }
  }, [permLoading, utente]);

  // Filtra menu in base ai permessi
  const visibleConfig = useMemo(() => {
    if (!utente) return MENU_CONFIG;
    return MENU_CONFIG
      .map(macro => {
        if (!macro.groups) return macro; // Dashboard — sempre visibile
        const filteredGroups = macro.groups
          .map(g => ({
            ...g,
            items: g.items.filter(item => !item.modulo || can(item.modulo, 'view')),
          }))
          .filter(g => g.items.length > 0);
        return filteredGroups.length > 0 ? { ...macro, groups: filteredGroups } : null;
      })
      .filter(Boolean) as MenuMacro[];
  }, [utente, can]);

  // Voci preferite come lista piatta (ordine del menù, icona della macro).
  // Derivata da visibleConfig: una voce senza più permesso sparisce anche qui.
  const preferitiVoci = useMemo(() => {
    const voci: { label: string; path: string; icon: React.ReactNode }[] = [];
    for (const macro of visibleConfig) {
      if (macro.path && preferitiSet.has(macro.path)) {
        voci.push({ label: macro.label, path: macro.path, icon: macro.icon });
      }
      macro.groups?.forEach(group => group.items.forEach(item => {
        if (preferitiSet.has(item.path)) {
          voci.push({ label: item.label, path: item.path, icon: macro.icon });
        }
      }));
    }
    return voci;
  }, [visibleConfig, preferitiSet]);

  // Risultati filtrati della ricerca menu
  const filteredConfig = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return null;
    const result: typeof visibleConfig = [];
    for (const macro of visibleConfig) {
      if (!macro.groups) {
        if (macro.label.toLowerCase().includes(q)) result.push(macro);
        continue;
      }
      const filteredGroups = macro.groups
        .map(g => ({ ...g, items: g.items.filter(item => item.label.toLowerCase().includes(q)) }))
        .filter(g => g.items.length > 0);
      if (filteredGroups.length > 0) result.push({ ...macro, groups: filteredGroups });
    }
    return result;
  }, [menuSearch, visibleConfig]);

  // Path del menu corrispondente alla URL corrente (best match).
  const activePath = useMemo(() => findActivePath(location.pathname), [location.pathname]);

  // Garantisce che macro/gruppo della voce attiva siano sempre aperti.
  // Non chiude mai nulla (per preservare macro che l'utente ha aperto a mano).
  useEffect(() => {
    setExpandedMacros(prev => {
      const next = { ...prev };
      let changed = false;
      MENU_CONFIG.forEach(macro => {
        if (isMacroActive(macro, activePath)) {
          const k = macroKey(macro);
          if (!next[k]) { next[k] = true; changed = true; }
        }
      });
      return changed ? next : prev;
    });
    setExpandedGroups(prev => {
      const next = { ...prev };
      let changed = false;
      MENU_CONFIG.forEach(macro => {
        if (!isMacroActive(macro, activePath)) return;
        macro.groups?.forEach(group => {
          if (isGroupActive(group, activePath)) {
            const k = groupKey(macro, group);
            if (!next[k]) { next[k] = true; changed = true; }
          }
        });
      });
      return changed ? next : prev;
    });
  }, [activePath]);

  const closeSidebar = () => setSidebarOpen(false);
  // Hamburger nell'header: su mobile apre/chiude il drawer, su desktop mostra/nasconde la sidebar
  const toggleSidebar = () => {
    if (isMobileViewport()) setSidebarOpen(o => !o);
    else setCollapsed(!sidebarCollapsed);
  };
  // Pulsante "chiudi" dentro la sidebar
  const hideSidebar = () => {
    if (isMobileViewport()) closeSidebar();
    else setCollapsed(true);
  };
  const handleItemClick = (path: string) => { navigate(path); closeSidebar(); setMenuSearch(''); };

  // Comprimi tutto / Espandi tutto: agisce su macro e gruppi visibili.
  // Dopo "comprimi tutto" la voce attiva resta chiusa finché non si naviga
  // altrove: l'effetto su activePath riapre solo al cambio di route.
  const isAllCollapsed = !visibleConfig.some(macro => macro.groups && expandedMacros[macroKey(macro)]);
  const toggleAllExpansion = () => {
    if (isAllCollapsed) {
      const macros: Record<string, boolean> = {};
      const groups: Record<string, boolean> = {};
      visibleConfig.forEach(macro => {
        if (!macro.groups) return;
        macros[macroKey(macro)] = true;
        macro.groups.forEach(group => { groups[groupKey(macro, group)] = true; });
      });
      setExpandedMacros(macros);
      setExpandedGroups(groups);
    } else {
      setExpandedMacros({});
      setExpandedGroups({});
    }
  };
  const toggleMacro = (macro: MenuMacro) =>
    setExpandedMacros(prev => ({ ...prev, [macroKey(macro)]: !prev[macroKey(macro)] }));
  const toggleGroup = (macro: MenuMacro, group: MenuGroup) =>
    setExpandedGroups(prev => ({ ...prev, [groupKey(macro, group)]: !prev[groupKey(macro, group)] }));

  // Stellina accanto a una voce navigabile: span (non button) perché vive
  // dentro il button della voce; stopPropagation per non navigare al click.
  const renderStar = (path: string) => {
    const attivo = preferitiSet.has(path);
    return (
      <span
        className={`bop-layout__fav-star${attivo ? ' bop-layout__fav-star--active' : ''}`}
        title={attivo ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
        aria-label={attivo ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
        onClick={e => { e.stopPropagation(); togglePreferito(path); }}
      >
        <UIIcon.Star filled={attivo} />
      </span>
    );
  };

  if (permLoading || !utente) {
    return (
      <div className="layout-loading">
        <div className="spinner" />
      </div>
    );
  }

  const pageTitle = title ?? getActiveLabel(activePath);

  return (
    <div className="layout">
      <Topbar />

      {/* ── Accent bar società ────────────────────────────────────────── */}
      <div
        className="layout-accent-bar"
        style={{ backgroundColor: coloreAccento ?? 'transparent' }}
      />

      <div className="layout-body">
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      <aside ref={sidebarRef} className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}${sidebarCollapsed ? ' sidebar--collapsed' : ''}`}>
        <div className="sidebar-header">
          <span className="sidebar-user-name">{utente.nome}</span>
          <div className="bop-layout__sidebar-actions">
            {menuTab === 'menu' && filteredConfig === null && (
              <button
                type="button"
                className="bop-layout__sidebar-action-btn"
                onClick={toggleAllExpansion}
                title={isAllCollapsed ? 'Espandi tutte le voci' : 'Comprimi tutte le voci'}
                aria-label={isAllCollapsed ? 'Espandi tutte le voci' : 'Comprimi tutte le voci'}
              >
                {isAllCollapsed ? <UIIcon.ChevronsDown /> : <UIIcon.ChevronsUp />}
              </button>
            )}
            <button
              type="button"
              className="bop-layout__sidebar-action-btn"
              onClick={hideSidebar}
              title="Chiudi il menù"
              aria-label="Chiudi il menù"
            >
              <UIIcon.PanelClose />
            </button>
          </div>
        </div>

        {/* ── Ricerca menu ──────────────────────────────────────────────── */}
        <div className="bop-layout__search-wrapper">
          <div className="bop-layout__search-container">
            <span className="bop-layout__search-icon">🔍</span>
            <input
              type="text"
              placeholder="Cerca nel menu..."
              value={menuSearch}
              onChange={e => setMenuSearch(e.target.value)}
              className="bop-layout__search-input"
            />
            {menuSearch && (
              <button
                onClick={() => setMenuSearch('')}
                className="bop-layout__search-clear"
                aria-label="Svuota ricerca"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* ── Tab Menù / Preferiti ─────────────────────────────────────── */}
        <div className="bop-layout__menu-tabs">
          <button
            className={`bop-layout__menu-tab${menuTab === 'menu' ? ' bop-layout__menu-tab--active' : ''}`}
            onClick={() => switchMenuTab('menu')}
          >
            Menù
          </button>
          <button
            className={`bop-layout__menu-tab${menuTab === 'preferiti' ? ' bop-layout__menu-tab--active' : ''}`}
            onClick={() => switchMenuTab('preferiti')}
          >
            Preferiti
            {preferitiVoci.length > 0 && (
              <span className="bop-layout__menu-tab-count">{preferitiVoci.length}</span>
            )}
          </button>
        </div>

        <nav className="sidebar-nav">
          {filteredConfig !== null ? (
            /* ── RISULTATI RICERCA ─────────────────────────────────────── */
            filteredConfig.length === 0 ? (
              <div className="bop-layout__search-empty">
                Nessuna voce trovata
              </div>
            ) : (
              filteredConfig.map((macro, macroIdx) => (
                <div key={macroIdx} className="sidebar-item-container">
                  {macro.path ? (
                    <button
                      className={`sidebar-macro ${macro.path === activePath ? 'active' : ''}`}
                      onClick={() => handleItemClick(macro.path!)}
                    >
                      <span className="sidebar-icon">{macro.icon}</span>
                      <span className="sidebar-macro-label">{highlight(macro.label, menuSearch)}</span>
                      {renderStar(macro.path)}
                    </button>
                  ) : (
                    <>
                      <div className="sidebar-macro bop-layout__macro-static">
                        <span className="sidebar-icon">{macro.icon}</span>
                        <span className="sidebar-macro-label">{macro.label}</span>
                      </div>
                      <div className="sidebar-macro-children open">
                        {macro.groups!.map((group, groupIdx) => (
                          <div key={groupIdx} className="sidebar-group-container">
                            <div className="sidebar-group bop-layout__group-static">
                              <span className="sidebar-group-label">{group.label}</span>
                            </div>
                            <div className="sidebar-group-children open">
                              {group.items.map((item, itemIdx) => {
                                const isActiveItem = item.path === activePath;
                                return (
                                  <button
                                    key={itemIdx}
                                    className={`sidebar-item ${isActiveItem ? 'active' : ''}`}
                                    onClick={() => handleItemClick(item.path)}
                                  >
                                    <span className="bop-layout__item-label">{highlight(item.label, menuSearch)}</span>
                                    {renderStar(item.path)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))
            )
          ) : menuTab === 'preferiti' ? (
            /* ── TAB PREFERITI — lista piatta ──────────────────────────── */
            preferitiVoci.length === 0 ? (
              <div className="bop-layout__fav-empty">
                Nessun preferito.<br />
                Clicca la stellina accanto a una voce del menù per aggiungerla qui.
              </div>
            ) : (
              preferitiVoci.map(voce => (
                <button
                  key={voce.path}
                  className={`sidebar-item${voce.path === activePath ? ' active' : ''}`}
                  onClick={() => handleItemClick(voce.path)}
                >
                  <span className="sidebar-icon">{voce.icon}</span>
                  <span className="bop-layout__item-label">{voce.label}</span>
                  {renderStar(voce.path)}
                </button>
              ))
            )
          ) : (
            /* ── MENU NORMALE ──────────────────────────────────────────── */
            visibleConfig.map((macro, macroIdx) => {
              const isMacroExpanded = !!expandedMacros[macroKey(macro)];
              const hasMacroActiveChild = isMacroActive(macro, activePath);

              return (
                <div key={macroIdx} className="sidebar-item-container">

                  {/* ── MACRO LEVEL ── */}
                  {macro.path ? (
                    <button
                      className={`sidebar-macro ${macro.path === activePath ? 'active' : ''}`}
                      onClick={() => handleItemClick(macro.path!)}
                    >
                      <span className="sidebar-icon">{macro.icon}</span>
                      <span className="sidebar-macro-label">{macro.label}</span>
                      {renderStar(macro.path)}
                    </button>
                  ) : (
                    <button
                      className={`sidebar-macro ${hasMacroActiveChild ? 'active-parent' : ''}`}
                      onClick={() => toggleMacro(macro)}
                    >
                      <span className="sidebar-icon">{macro.icon}</span>
                      <span className="sidebar-macro-label">{macro.label}</span>
                      <span className={`sidebar-chevron ${isMacroExpanded ? 'open' : ''}`}>
                        <UIIcon.ChevronDown />
                      </span>
                    </button>
                  )}

                  {/* ── GROUP LEVEL ── */}
                  {macro.groups && (
                    <div className={`sidebar-macro-children ${isMacroExpanded ? 'open' : ''}`}>
                      {macro.groups.map((group, groupIdx) => {
                        const isGroupExpanded = !!expandedGroups[groupKey(macro, group)];
                        const hasGroupActiveChild = isGroupActive(group, activePath);

                        return (
                          <div key={groupIdx} className="sidebar-group-container">
                            <button
                              className={`sidebar-group ${hasGroupActiveChild ? 'active-parent' : ''}`}
                              onClick={() => toggleGroup(macro, group)}
                            >
                              <span className="sidebar-group-label">{group.label}</span>
                              <span className={`sidebar-chevron ${isGroupExpanded ? 'open' : ''}`}>
                                <UIIcon.ChevronDown />
                              </span>
                            </button>

                            {/* ── ITEM LEVEL ── */}
                            <div className={`sidebar-group-children ${isGroupExpanded ? 'open' : ''}`}>
                              {group.items.map((item, itemIdx) => {
                                const isActiveItem = item.path === activePath;
                                return (
                                  <button
                                    key={itemIdx}
                                    className={`sidebar-item ${isActiveItem ? 'active' : ''}`}
                                    onClick={() => handleItemClick(item.path)}
                                  >
                                    <span className="bop-layout__item-label">{item.label}</span>
                                    {renderStar(item.path)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>
              );
            })
          )}
        </nav>

      </aside>

      <div className="layout-main">
        <header className="layout-header">
          <button
            className="layout-menu-btn"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Mostra il menù' : 'Nascondi il menù'}
            aria-label={sidebarCollapsed ? 'Mostra il menù' : 'Nascondi il menù'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1 className="layout-header-title">
            {pageTitle}
            {societaAttiva && (
              <span className="bop-layout__title-societa"> · {societaAttiva.nome}</span>
            )}
          </h1>
        </header>
        <main className="layout-content">
          {children}
        </main>
      </div>

      </div>{/* layout-body */}

      {/* ── Toast 403 ─────────────────────────────────────────────────── */}
      {toast403 && (
        <Toast403 message={toast403} onClose={() => setToast403(null)} />
      )}
    </div>
  );
}
